import { call, put, all, spawn, takeLatest } from 'redux-saga/effects';

import request from 'utils/request';
import {
  cancellableRequest,
  isAbortError,
} from 'utils/cancellableRequest';
import { TEST, GET_SETTLEMENT_HOLIDAYS_URL } from 'utils/constants';
import { useQuery } from 'utils/common';

import {
  UPSERT_MERCHANT_REG_DETAILS_URL,
  DOWNLOAD_DOCUMENT_URL,
  UPLOAD_DOCS_URL,
  GET_PAYMENT_METHODS_URL,
} from 'constants/url';

import { showToast } from '../Toast/actions';

import {
  disableLoaderAction,
  handlePaymentMethodsResponse,
  handleOtherAvailableModesResponse,
  handlePaypalConfigResponse,
  resetPaymentMethodCardLoaderAction,
  handleUpdateDefaultPaypalCurrency,
  handleDeactivatePaypalCurrency,
  handleActivatePaypalCurrency,
  getOtherAvailableModesAction,
  savePaytmDataAction,
  savePaytmProdConfigAction,
  savePaytmTestConfigAction,
} from './actions';
import {
  PAYPAL,
  PAYTM,
  GET_PAYMENT_METHODS,
  GET_OTHER_AVAILABLE_MODES,
  GET_OTHER_AVAILABLE_MODES_URL,
  GET_PAYPAL_CONFIG,
  GET_PAYPAL_CONFIG_URL,
  GET_ACTIVE_PAYPAL_CURRENCIES_URL,
  GET_DEFAULT_PAYPAL_CURRENCY_URL,
  GET_PAYPAL_CURRENCIES_URL,
  INITIATE_PAYPAL,
  INITIATE_PAYPAL_URL,
  UPDATE_DEFAULT_PAYPAL_CURRENCY,
  UPDATE_DEFAULT_PAYPAL_CURRENCY_URL,
  ACTIVATE_PAYPAL_CURRENCY,
  ACTIVATE_PAYPAL_CURRENCY_URL,
  DEACTIVATE_PAYPAL_CURRENCY,
  DEACTIVATE_PAYPAL_CURRENCY_URL,
  SAVE_PAYPAL_DATA,
  SAVE_PAYPAL_DATA_URL,
  RESET_PAYPAL_CONFIG,
  RESET_PAYPAL_CONFIG_URL,
  ENABLE_PAYMENT_METHOD,
  ENABLE_PAYMENT_METHOD_URL,
  GET_PAYTM_DATA,
  GET_PAYTM_TEST_CONFIG,
  GET_PAYTM_TEST_CONFIG_URL,
  GET_PAYTM_PROD_CONFIG_URL,
  GET_PAYTM_CHECKSUMHASH,
  GET_PAYTM_CHECKSUMHASH_URL,
  ADD_PAYTM_PROD_CONFIG,
  ADD_PAYTM_TEST_CONFIG,
  ADD_PAYTM_TEST_CONFIG_URL,
  ADD_PAYTM_PROD_CONFIG_URL,
  RESET_PAYTM_WALLET,
  RESET_PAYTM_WALLET_URL,
  UPDATE_PAYTM_TEST_CONFIG_URL,
  GET_PAYMENT_METHOD_BY_STATUS_URL,
  GET_DOCS_PER_PAYMENT_METHOD,
  GET_DOCS_PER_PAYMENT_METHOD_URL,
  SAVE_UPLOAD_DOCUMENTS_FORM,
  DOWNLOAD_DOCUMENT,
  PAYMENT_METHODS_STATUS,
  GET_ALL_MODES_OLD_URL,
  SETTLEMENTS_ACTIVE_STATUS,
} from './constants';

function* getAllModesUsingOldAPI() {
  try {
    const { url, method } = GET_ALL_MODES_OLD_URL;
    const response = yield call(request, url, { method });
    yield put(
      handleOtherAvailableModesResponse([
        response,
        { paymentModes: [], hideEnablePaymentMethods: true },
      ]),
    );
  } catch (e) {
    yield put(disableLoaderAction());
    yield put(showToast(e));
  }
}

function* getOtherAvailableAndModesByStatus() {
  try {
    const promiseArr = [];

    // eslint-disable-next-line redux-saga/yield-effects
    promiseArr.push(call(request, GET_OTHER_AVAILABLE_MODES_URL.url));

    promiseArr.push(
      // eslint-disable-next-line redux-saga/yield-effects
      call(request, GET_PAYMENT_METHOD_BY_STATUS_URL.url, {
        queryString: {
          paymentModeStatus: Object.keys(PAYMENT_METHODS_STATUS).join(','),
        },
      }),
    );
    const response = yield all(promiseArr);
    yield put(handleOtherAvailableModesResponse(response));
  } catch (e) {
    yield put(disableLoaderAction());
    yield put(showToast(e));
  }
}

export function* getOtherAvailableModes() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const query = useQuery();

  // if env is test then call the old endpoint
  if (query.get('env') === TEST) {
    yield getAllModesUsingOldAPI();
  } else {
    // next-date is active then only call the following apis
    try {
      const { url, method } = GET_SETTLEMENT_HOLIDAYS_URL;
      const settlementHolidayResponse = yield call(cancellableRequest, url, { method });
      if (
        settlementHolidayResponse &&
        settlementHolidayResponse.status === SETTLEMENTS_ACTIVE_STATUS
      ) {
        yield getOtherAvailableAndModesByStatus();
      } else {
        yield getAllModesUsingOldAPI();
      }
    } catch (e) {
      if (isAbortError(e)) return;
      yield getAllModesUsingOldAPI();
    }
  }
}

export function* getPaymentMethods() {
  try {
    const { url, method } = GET_PAYMENT_METHODS_URL;
    const finalUrl = `${url}?currency=INR&amount=10`;
    const response = yield call(cancellableRequest, finalUrl, { method });
    yield put(handlePaymentMethodsResponse(response));
    yield put(getOtherAvailableModesAction());
  } catch (err) {
    yield put(disableLoaderAction());
    if (isAbortError(err)) return;
    yield put(showToast(err));
  }
}

export function* getPaypalConfig() {
  try {
    const { url, method } = GET_PAYPAL_CONFIG_URL;
    const finalUrl = `${url}?count=1&version=v2&active=true`;
    const { content = [] } = yield call(cancellableRequest, finalUrl, { method });
    const activeAccount = content.find(obj => !!obj.active);
    const result = { activeAccount };

    if (activeAccount) {
      const promiseArr = [
        GET_ACTIVE_PAYPAL_CURRENCIES_URL,
        GET_PAYPAL_CURRENCIES_URL,
        GET_DEFAULT_PAYPAL_CURRENCY_URL,
        // eslint-disable-next-line redux-saga/yield-effects
      ].map(obj => call(cancellableRequest, obj.url, { method: obj.method }));

      const [activeCurrencies, paypalCurrencies, defaultCurrency] = yield all(
        promiseArr,
      );

      result.activeCurrencies = activeCurrencies;
      result.paypalCurrencies = paypalCurrencies;
      result.defaultCurrency = defaultCurrency;
    }
    yield put(handlePaypalConfigResponse(result));
  } catch (err) {
    yield put(disableLoaderAction());
    if (isAbortError(err)) return;
    yield put(resetPaymentMethodCardLoaderAction(PAYPAL));
    yield put(showToast(err));
  }
}

export function* initiatePaypal({ data: { cb, errorCb } }) {
  try {
    const { url: requestUrl, method } = INITIATE_PAYPAL_URL;
    const { url } = yield call(request, requestUrl, { method });
    cb(url);
  } catch (err) {
    if (errorCb) errorCb();
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* updateDefaultPaypalCurrency({
  data: { successCb, errorCb, currencyCode },
}) {
  try {
    const { url, method } = UPDATE_DEFAULT_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (successCb) successCb();
    yield put(handleUpdateDefaultPaypalCurrency(response));
  } catch (err) {
    if (errorCb) errorCb();
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* activatePaypalCurrency({
  data: { successCb, currencyCode, errorCb },
}) {
  try {
    const { url, method } = ACTIVATE_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (successCb) successCb();
    yield put(handleActivatePaypalCurrency(response));
  } catch (err) {
    if (errorCb) errorCb();
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* deactivatePaypalCurrency({ data: { cb, currencyCode } }) {
  try {
    const { url, method } = DEACTIVATE_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (cb) cb();
    yield put(handleDeactivatePaypalCurrency(response));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* savePaypalData({ data: { data, successCb, errorCb } }) {
  try {
    const { url, method } = SAVE_PAYPAL_DATA_URL;
    yield call(request, url, {
      method,
      body: JSON.stringify(data),
    });
    successCb();
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  }
}

export function* enablePaymentMethod({
  data: { successCb, errorCb, isInstantActive, ...data },
}) {
  try {
    const { url, method } = ENABLE_PAYMENT_METHOD_URL;
    yield call(request, url, { method, body: JSON.stringify(data) });
    successCb();
    if (isInstantActive)
      yield put(
        showToast({
          message: `Payment method enabled successfully.`,
          type: 'SUCCESS',
        }),
      );
  } catch (err) {
    errorCb(err);
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* resetPaypalConfig({ data: { successCb, errorCb } }) {
  try {
    const { url, method } = RESET_PAYPAL_CONFIG_URL;
    yield call(request, url, { method });
    successCb();
    yield put(
      showToast({
        message: 'Paypal Deactivated Successfully',
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  }
}

export function* getPaytmData() {
  try {
    const promiseArr = [
      GET_PAYTM_PROD_CONFIG_URL,
      GET_PAYTM_TEST_CONFIG_URL,
      // eslint-disable-next-line redux-saga/yield-effects
    ].map(obj => call(cancellableRequest, obj.url, { method: obj.method }));

    const [paytmProdConfig, paytmTestConfig] = yield all(promiseArr);

    yield put(savePaytmDataAction({ paytmProdConfig, paytmTestConfig }));
  } catch (err) {
    if (isAbortError(err)) return;
    yield put(resetPaymentMethodCardLoaderAction(PAYTM));
    yield put(showToast(err));
  }
}

export function* getPaytmTestConfig() {
  try {
    const { url, method } = GET_PAYTM_TEST_CONFIG_URL;
    const response = yield call(cancellableRequest, url, { method });
    yield put(savePaytmTestConfigAction(response));
  } catch (err) {
    if (isAbortError(err)) return;
    yield put(showToast(err));
  }
}

export function* getPaytmCheckSumHash({ data }) {
  const { paytmParams, merchantPaytmKey, cb } = data;
  try {
    const { url, method } = GET_PAYTM_CHECKSUMHASH_URL;
    const payload = {
      merchantPaytmId: paytmParams.MID,
      merchantPaytmWebsiteUrl: paytmParams.WEBSITE,
      merchantPaytmChannelId: paytmParams.CHANNEL_ID,
      merchantPaytmIndustryType: paytmParams.INDUSTRY_TYPE_ID,
      paytmOrderId: paytmParams.ORDER_ID,
      customerId: paytmParams.CUST_ID,
      txnAmount: paytmParams.TXN_AMOUNT,
      callBackUrl: paytmParams.CALLBACK_URL,
      email: paytmParams.EMAIL,
      merchantPaytmKey,
    };
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, url, options);
    cb({ ...paytmParams, CHECKSUMHASH: response });
  } catch (err) {
    yield put(showToast(err));
  }
}

export function* addPaytmProdConfig({ data }) {
  const { payload, actions, cb } = data;
  try {
    const { url, method } = ADD_PAYTM_PROD_CONFIG_URL;
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, url, options);
    yield put(savePaytmProdConfigAction(response));
    actions.setSubmitting(false);
    cb(false);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(showToast(err));
  }
}

export function* addPaytmTestConfig({ data }) {
  const { payload, actions, isEditMode, cbShowForm, cbEditMode } = data;
  try {
    const { url, method } = isEditMode
      ? UPDATE_PAYTM_TEST_CONFIG_URL
      : ADD_PAYTM_TEST_CONFIG_URL;
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, url, options);
    yield put(savePaytmTestConfigAction(response));
    actions.setSubmitting(false);
    cbShowForm(false);
    if (cbEditMode) cbEditMode(false);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(showToast(err));
  }
}

export function* resetPaytmWallet({ data }) {
  const { successCb, errorCb } = data;
  try {
    const { url, method } = RESET_PAYTM_WALLET_URL;
    yield call(request, url, { method });
    successCb();
    yield put(
      showToast({
        message: 'PayTM Deactivated Successfully',
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  }
}

export function* getDocsPerPaymentMethod({
  data: { data, successCb, errorCb },
}) {
  try {
    const { url, method } = GET_DOCS_PER_PAYMENT_METHOD_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString: data,
    };
    const response = yield call(cancellableRequest, url, options);
    successCb(response);
  } catch (e) {
    if (isAbortError(e)) {
      errorCb();
      return;
    }
    errorCb();
    yield put(showToast(e));
  }
}

export function* downloadDocuments({ data: { data, successCb } }) {
  try {
    const { url, method } = DOWNLOAD_DOCUMENT_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString: data,
    };
    const response = yield call(cancellableRequest, url, options);
    successCb(response);
  } catch (e) {
    if (isAbortError(e)) return;
    yield put(showToast(e));
  }
}

export function* saveUploadDocumentsForm({
  data: {
    data: { files, regDetails, paymentModeCode, showError },
    successCb,
    errorCb,
  },
}) {
  try {
    const promiseArr = [];
    if (files) {
      const { url, method } = UPLOAD_DOCS_URL;
      files.forEach(obj => {
        const { docType, file } = obj;
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('docType', docType);
        promiseArr.push(
          // eslint-disable-next-line redux-saga/yield-effects
          call(request, url, { method, body: formData }),
        );
      });
    }
    if (regDetails) {
      const { url, method } = UPSERT_MERCHANT_REG_DETAILS_URL;
      promiseArr.push(
        // eslint-disable-next-line redux-saga/yield-effects
        call(request, url, { method, body: JSON.stringify({ regDetails }) }),
      );
    }
    yield all(promiseArr);
    // once the documents and input fields are saved make the enable payment methods call
    const { url, method } = ENABLE_PAYMENT_METHOD_URL;
    yield call(request, url, {
      method,
      body: JSON.stringify({ paymentModeCode }),
    });

    successCb();
  } catch (e) {
    showError(e);
    errorCb(e);
    yield put(showToast(e));
  }
}

export function* getPaymentMethodsSaga() {
  yield takeLatest(GET_PAYMENT_METHODS, getPaymentMethods);
}

export function* getOtherAvailableModesSaga() {
  yield takeLatest(GET_OTHER_AVAILABLE_MODES, getOtherAvailableModes);
}

export function* getPaypalConfigSaga() {
  yield takeLatest(GET_PAYPAL_CONFIG, getPaypalConfig);
}

export function* initiatePaypalSaga() {
  yield takeLatest(INITIATE_PAYPAL, initiatePaypal);
}

export function* updateDefaultPaypalCurrencySaga() {
  yield takeLatest(UPDATE_DEFAULT_PAYPAL_CURRENCY, updateDefaultPaypalCurrency);
}

export function* activatePaypalCurrencySaga() {
  yield takeLatest(ACTIVATE_PAYPAL_CURRENCY, activatePaypalCurrency);
}

export function* deactivatePaypalCurrencySaga() {
  yield takeLatest(DEACTIVATE_PAYPAL_CURRENCY, deactivatePaypalCurrency);
}

export function* savePaypalDataSaga() {
  yield takeLatest(SAVE_PAYPAL_DATA, savePaypalData);
}

export function* resetPaypalConfigSaga() {
  yield takeLatest(RESET_PAYPAL_CONFIG, resetPaypalConfig);
}

export function* enablePaymentMethodSaga() {
  yield takeLatest(ENABLE_PAYMENT_METHOD, enablePaymentMethod);
}

export function* getPaytmDataSaga() {
  yield takeLatest(GET_PAYTM_DATA, getPaytmData);
}

export function* getPaytmTestConfigSaga() {
  yield takeLatest(GET_PAYTM_TEST_CONFIG, getPaytmTestConfig);
}

export function* getPaytmCheckSumHashSaga() {
  yield takeLatest(GET_PAYTM_CHECKSUMHASH, getPaytmCheckSumHash);
}

export function* addPaytmProdConfigSaga() {
  yield takeLatest(ADD_PAYTM_PROD_CONFIG, addPaytmProdConfig);
}

export function* addPaytmTestConfigSaga() {
  yield takeLatest(ADD_PAYTM_TEST_CONFIG, addPaytmTestConfig);
}

export function* resetPaytmWalletSaga() {
  yield takeLatest(RESET_PAYTM_WALLET, resetPaytmWallet);
}

export function* getDocsPerPaymentMethodSaga() {
  yield takeLatest(GET_DOCS_PER_PAYMENT_METHOD, getDocsPerPaymentMethod);
}

export function* saveUploadDocumentsFormSaga() {
  yield takeLatest(SAVE_UPLOAD_DOCUMENTS_FORM, saveUploadDocumentsForm);
}

export function* downloadDocumentsSaga() {
  yield takeLatest(DOWNLOAD_DOCUMENT, downloadDocuments);
}

// Individual exports for testing
export default function* paymentMethodsSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    getPaymentMethodsSaga,
    getOtherAvailableModesSaga,
    getPaypalConfigSaga,
    initiatePaypalSaga,
    updateDefaultPaypalCurrencySaga,
    activatePaypalCurrencySaga,
    deactivatePaypalCurrencySaga,
    savePaypalDataSaga,
    resetPaypalConfigSaga,
    enablePaymentMethodSaga,
    getPaytmDataSaga,
    getPaytmTestConfigSaga,
    getPaytmCheckSumHashSaga,
    addPaytmProdConfigSaga,
    addPaytmTestConfigSaga,
    resetPaytmWalletSaga,
    getDocsPerPaymentMethodSaga,
    saveUploadDocumentsFormSaga,
    downloadDocumentsSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
